import { Link, useParams } from "react-router-dom";
import { SERVICES, CLINIC } from "../data/content";
import { ArrowRight, CheckCircle2, Flower2, Trophy } from "lucide-react";
import LeadForm from "../components/site/LeadForm";
import WhatsAppButton from "../components/site/WhatsAppButton";

const FEATURED_PROGRAMS = [
  {
    to: "/team/amit-sharma",
    eyebrow: "Featured programme · Sports & Rehabilitation",
    Icon: Trophy,
    title: "Sports Rehab with",
    doctor: "Dr. Amit Sharma",
    description:
      "ACL, PCL, post-surgical knee, return-to-sport testing and full sports rehabilitation — led by our Fellowship-trained clinic head with 17+ years of clinical experience.",
    chips: ["ACL Rehab", "Post-Surgical", "Return-to-Sport", "Sports Injuries"],
    cta: "Explore Sports Rehab",
    bg: "url('https://images.unsplash.com/photo-1517836357463-d25dfeac3438?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.0.3&q=85&w=900')",
    testid: "svc-feature-sports-rehab",
  },
  {
    to: "/maternal-care",
    eyebrow: "Featured programme · Women's Health",
    Icon: Flower2,
    title: "Maternal Care with",
    doctor: "Dr. Manishika Patait",
    description:
      "Birth classes, childbirth & lactation education, prenatal & postnatal exercise and yoga — a calm, evidence-informed pathway led by a CAPPA-certified educator with 13+ years of experience.",
    chips: ["Birth Classes", "Prenatal Yoga", "Postnatal Recovery", "Lactation Support"],
    cta: "Explore Maternal Care",
    bg: "url('https://images.unsplash.com/photo-1518578255002-16f81a4c9be9?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.0.3&q=85&w=900')",
    testid: "svc-feature-maternal-care",
  },
];

export function ServicesList() {
  return (
    <div className="container-wide py-16">
      <div className="max-w-3xl">
        <div className="eyebrow mb-3">Our services</div>
        <h1 className="font-heading text-4xl md:text-6xl text-brand-ink leading-[1.05]">Care that starts with a proper assessment.</h1>
        <p className="text-brand-ink2 mt-5">
          Techniques don't fix people — good clinical reasoning does. Every service listed here begins with a personalised assessment, then integrates hands-on care with exercise-based rehabilitation.
        </p>
      </div>

      {/* Featured programmes led by named specialists */}
      <div className="mt-10 grid md:grid-cols-2 gap-5">
        {FEATURED_PROGRAMS.map(({ to, eyebrow, Icon, title, doctor, description, chips, cta, bg, testid }) => (
          <Link
            key={to}
            to={to}
            data-testid={testid}
            className="block group rounded-[24px] overflow-hidden bg-brand-blue-dark text-brand-cream hover:shadow-[0_16px_50px_rgb(0,0,0,0.15)] transition-shadow"
          >
            <div className="grid grid-cols-5 gap-0 items-stretch h-full">
              <div className="col-span-3 p-6 md:p-8">
                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-brand-amber">
                  <Icon size={14} /> {eyebrow}
                </div>
                <h2 className="font-heading text-2xl md:text-3xl mt-3 leading-tight">
                  {title} <span className="text-brand-amber">{doctor}</span>
                </h2>
                <p className="text-brand-cream/80 mt-3 text-sm">{description}</p>
                <div className="mt-4 flex flex-wrap gap-1.5 text-xs">
                  {chips.map((c) => (
                    <span key={c} className="bg-white/10 text-brand-cream rounded-full px-2.5 py-1">
                      {c}
                    </span>
                  ))}
                </div>
                <span className="mt-6 inline-flex items-center gap-1.5 text-sm text-brand-amber group-hover:gap-2 transition-all">
                  {cta} <ArrowRight size={16} />
                </span>
              </div>
              <div
                className="col-span-2 min-h-[220px] bg-cover bg-center"
                style={{ backgroundImage: bg }}
                aria-hidden="true"
              />
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {SERVICES.map((s) => (
          <Link key={s.slug} to={`/services/${s.slug}`} className="card-soft p-6 group hover:shadow-[0_16px_50px_rgb(0,0,0,0.08)] transition-shadow" data-testid={`svc-${s.slug}`}>
            <div className="text-xs uppercase tracking-widest text-brand-terracotta">{s.tag}</div>
            <h3 className="font-heading text-xl text-brand-ink mt-2 group-hover:text-brand-olive">{s.title}</h3>
            <p className="text-sm text-brand-ink2 mt-2">{s.blurb}</p>
            <div className="mt-4 text-sm text-brand-olive inline-flex items-center gap-1">Learn more <ArrowRight size={14}/></div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) {
    return (
      <div className="container-wide py-24 text-center">
        <h1 className="font-heading text-3xl text-brand-ink">Service not found</h1>
        <Link to="/services" className="btn-outline mt-6">Back to services</Link>
      </div>
    );
  }
  const waMsg = `Hello Physio Hyve, I'd like information about ${service.title}.`;
  return (
    <div>
      <section className="container-wide pt-16 pb-10">
        <div className="text-xs uppercase tracking-widest text-brand-terracotta">{service.tag}</div>
        <h1 className="font-heading text-4xl md:text-6xl text-brand-ink mt-2 leading-[1.05] max-w-3xl">{service.title}</h1>
        <p className="text-brand-ink2 mt-5 max-w-2xl">{service.blurb} Every rehabilitation plan begins with an individual assessment, movement testing and clinical reasoning tailored to your goals.</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/book" className="btn-primary">Book Your Assessment <ArrowRight size={16}/></Link>
          <WhatsAppButton message={waMsg}>Ask about {service.title}</WhatsAppButton>
        </div>
      </section>

      <section className="container-wide py-10 grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2 space-y-8">
          <div>
            <h2 className="font-heading text-2xl text-brand-ink">Who this may help</h2>
            <ul className="mt-3 space-y-2">
              {["Patients with persistent or recurring symptoms", "People recovering from an injury or surgery", "Those returning to sport, work or daily activities", "Anyone whose previous care hasn't fully worked"].map((t) => (
                <li key={t} className="flex gap-2 text-brand-ink2"><CheckCircle2 size={16} className="text-brand-olive mt-1 shrink-0"/> {t}</li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="font-heading text-2xl text-brand-ink">What to expect during assessment</h2>
            <p className="mt-3 text-brand-ink2">A structured conversation about your history, symptoms and goals, followed by movement testing, strength assessment and clinical reasoning. We explain what we find, what a plan may look like, and answer your questions before any treatment begins.</p>
          </div>
          <div>
            <h2 className="font-heading text-2xl text-brand-ink">The Physio Hyve approach</h2>
            <p className="mt-3 text-brand-ink2">We combine appropriate hands-on techniques with progressive exercise-based rehabilitation. Where advanced modalities such as dry needling, taping or specialised techniques are indicated, they support — not replace — your active recovery.</p>
          </div>
          <div className="card-soft p-6">
            <h3 className="font-heading text-lg text-brand-ink">Duration & frequency</h3>
            <p className="text-brand-ink2 mt-2 text-sm">Session frequency and total duration depend on the specific condition, assessment findings, goals and how you respond to rehabilitation. We share a realistic plan after your first visit.</p>
          </div>
        </div>
        <aside className="md:sticky md:top-24 self-start">
          <LeadForm category={slug} sourcePage={`service-${slug}`} defaultConcern={`Interested in ${service.title}`} />
        </aside>
      </section>
    </div>
  );
}
